/* eslint-disable unicorn/no-null */
import type { FC } from 'react';
import { useEffect, useMemo, useRef } from 'react';
// mantine
import {
    Button,
    Divider,
    Group,
    Select,
    Switch,
    Textarea,
    TextInput,
    UnstyledButton
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
// utils
import { useFormik } from 'formik';
import type {
    TV3CreateProjectData,
    TV3Project,
    TV3UpdateProjectData
} from '@newo/api/gateway';
import { projectFormV2Schema } from '@/pages/Projects/components/ProjectForm/ProjectForm.schema';
import {
    useCustomerLinkQuery,
    useDeleteCustomerLinkMutation,
    useUpsertCustomerLinkMutation
} from '@/pages/Project/components/Files/CustomerLinks.queries';
import {
    useV3RegistriesQuery,
    useV3RegistryModulesQuery,
    useV3RegistryModuleVersionsQuery
} from '@/pages/Project/components/Files/RegistryModules.queries';

type ProjectFormProps = {
    project?: TV3Project;
    onClose: () => void;
    onSubmit: (
        data: TV3CreateProjectData | TV3UpdateProjectData,
        projectId?: string
    ) => Promise<void>;
};

type ProjectFormValues = {
    idn: string;
    title: string;
    description: string;
    activate_from_module: boolean;
    registry_idn: string | null;
    module_idn: string | null;
    module_version: string | null;
};

const LATEST_VERSION = 'latest_version';

const ProjectForm: FC<ProjectFormProps> = ({ project, onClose, onSubmit }) => {
    const isEditProject = !!project;

    const { data: customerLink } = useCustomerLinkQuery(
        isEditProject ? project.idn : undefined
    );
    const upsertCustomerLink = useUpsertCustomerLinkMutation();
    const deleteCustomerLink = useDeleteCustomerLinkMutation();

    const existingCustomerLinkId =
        isEditProject && customerLink ? customerLink.id : null;

    const upsertLinkOrWarn = async (
        formData: ProjectFormValues,
        targetProjectIdn: string
    ) => {
        if (!formData.registry_idn || !formData.module_idn) {
            return;
        }
        try {
            await upsertCustomerLink.mutateAsync({
                projectIdn: targetProjectIdn,
                registryIdn: formData.registry_idn,
                moduleIdn: formData.module_idn,
                moduleVersion:
                    formData.module_version === LATEST_VERSION
                        ? null
                        : formData.module_version
            });
        } catch {
            notifications.show({
                color: 'yellow',
                title: 'Project saved',
                message: 'Module activation failed — please activate manually'
            });
        }
    };

    const deleteLinkOrWarn = async (
        customerLinkId: string,
        targetProjectIdn: string
    ) => {
        try {
            await deleteCustomerLink.mutateAsync({
                customerLinkId,
                projectIdn: targetProjectIdn
            });
        } catch {
            notifications.show({
                color: 'yellow',
                title: 'Project saved',
                message:
                    'Module deactivation failed — please deactivate manually'
            });
        }
    };

    const handleFormSubmit = async (formData: ProjectFormValues) => {
        const payload: TV3CreateProjectData | TV3UpdateProjectData = {
            idn: formData.idn,
            title: formData.title,
            description: formData.description
        };
        await onSubmit(payload, project?.id);

        const targetProjectIdn = formData.idn;
        if (formData.activate_from_module) {
            await upsertLinkOrWarn(formData, targetProjectIdn);
            return;
        }

        const shouldDeactivateModuleLink =
            existingCustomerLinkId && customerLink?.registry_idn;
        if (shouldDeactivateModuleLink) {
            await deleteLinkOrWarn(existingCustomerLinkId, targetProjectIdn);
        }
    };

    const initialValues: ProjectFormValues = {
        idn: project?.idn ?? '',
        title: project?.title ?? '',
        description: project?.description ?? '',
        registry_idn: null,
        activate_from_module: false,
        module_idn: null,
        module_version: LATEST_VERSION
    };

    const formik = useFormik<ProjectFormValues>({
        initialValues,
        validationSchema: projectFormV2Schema,
        async onSubmit(formData) {
            await handleFormSubmit(formData);
            formik.setSubmitting(false);
        }
    });

    const { data: v3Registries = [] } = useV3RegistriesQuery();
    const selectedV3RegistryId = v3Registries.find(
        (registry) => registry.idn === formik.values.registry_idn
    )?.id;
    const { data: v3Modules = [] } =
        useV3RegistryModulesQuery(selectedV3RegistryId);
    const { data: v3VersionsData } = useV3RegistryModuleVersionsQuery(
        formik.values.registry_idn ?? undefined,
        formik.values.module_idn ?? undefined
    );
    const hasPrefilledRef = useRef(false);

    useEffect(() => {
        if (!isEditProject) {
            return;
        }
        if (hasPrefilledRef.current) {
            return;
        }

        if (customerLink === undefined) {
            return;
        }

        if (customerLink?.registry_idn) {
            formik.setFieldValue('activate_from_module', true);
            formik.setFieldValue('registry_idn', customerLink.registry_idn);
            formik.setFieldValue('module_idn', customerLink.module_idn);
            formik.setFieldValue(
                'module_version',
                customerLink.module_version ?? LATEST_VERSION
            );
        }
        hasPrefilledRef.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerLink, isEditProject]);

    const handleActivateFromModuleChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        formik.setFieldValue('activate_from_module', event.target.checked);
    };

    const handleV2RegistryIdnChange = (value: string | null) => {
        formik.setFieldValue('registry_idn', value);
        formik.setFieldValue('module_idn', null);
        formik.setFieldValue('module_version', LATEST_VERSION, false);
    };

    const handleV2ModuleIdnChange = (value: string | null) => {
        formik.setFieldValue('module_idn', value);
        formik.setFieldValue('module_version', LATEST_VERSION, false);
    };

    const handleV2ModuleVersionChange = (value: string | null) => {
        formik.setFieldValue('module_version', value);
    };

    const registryOptions = useMemo(
        () => v3Registries.map((registry) => registry.idn),
        [v3Registries]
    );
    const moduleOptions = useMemo(
        () => Array.from(new Set(v3Modules.map((module) => module.idn))),
        [v3Modules]
    );
    const versionOptions = useMemo(
        () => [
            { value: LATEST_VERSION, label: 'Latest version' },
            ...(v3VersionsData?.items ?? []).map(({ version }) => ({
                label: version,
                value: version
            }))
        ],
        [v3VersionsData?.items]
    );

    return (
        <form
            autoComplete="off"
            className="flex flex-col gap-3"
            onSubmit={formik.handleSubmit}
        >
            <TextInput
                withAsterisk
                description="Unique Identifying Name"
                error={
                    formik.touched.idn && formik.values.idn
                        ? formik.errors.idn
                        : undefined
                }
                label="Idn"
                name="idn"
                placeholder="Idn"
                value={formik.values.idn}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />

            <TextInput
                withAsterisk
                description="Project title"
                error={
                    formik.touched.title && formik.values.title
                        ? formik.errors.title
                        : undefined
                }
                label="Title"
                name="title"
                placeholder="Project title"
                value={formik.values.title}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />

            <Textarea
                description="Project description"
                error={
                    formik.touched.description && formik.values.description
                        ? formik.errors.description
                        : undefined
                }
                label="Description"
                name="description"
                placeholder="Project description"
                rows={4}
                value={formik.values.description}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />

            <Divider className="mt-2" label="Module" labelPosition="left" />

            <Switch
                checked={formik.values.activate_from_module}
                label="Activate from module"
                name="activate_from_module"
                onChange={handleActivateFromModuleChange}
            />

            {formik.values.activate_from_module ? (
                <>
                    <Select
                        withAsterisk
                        comboboxProps={{ withinPortal: false }}
                        data={registryOptions}
                        error={
                            formik.touched.registry_idn
                                ? formik.errors.registry_idn
                                : null
                        }
                        label="Registry"
                        name="registry_idn"
                        placeholder="Select Registry"
                        value={formik.values.registry_idn}
                        onChange={handleV2RegistryIdnChange}
                    />

                    <Select
                        clearable
                        withAsterisk
                        comboboxProps={{ withinPortal: false }}
                        data={moduleOptions}
                        disabled={!formik.values.registry_idn}
                        error={
                            formik.touched.module_idn
                                ? formik.errors.module_idn
                                : null
                        }
                        label="Module"
                        name="module_idn"
                        placeholder="Select Module"
                        value={formik.values.module_idn}
                        onChange={handleV2ModuleIdnChange}
                    />

                    <Select
                        clearable
                        searchable
                        data={versionOptions}
                        disabled={!formik.values.module_idn}
                        label="Module version"
                        name="module_version"
                        placeholder="Select Module version"
                        value={formik.values.module_version}
                        onChange={handleV2ModuleVersionChange}
                    />
                </>
            ) : null}

            <Group justify="space-between">
                <UnstyledButton
                    className="text-xs text-gray-6"
                    onClick={onClose}
                >
                    Cancel
                </UnstyledButton>
                <Button
                    disabled={!formik.isValid}
                    loading={formik.isSubmitting}
                    size="xs"
                    type="submit"
                >
                    {project ? 'Save' : 'Create'}
                </Button>
            </Group>
        </form>
    );
};

export default ProjectForm;
